<template>
  <div class="w100p h100p minh50">
    <div class="h30" v-if="optionsData['temperature'] && vitalsLength">
      <div class="mr2 display-flex justify-between">
        <h4 class="ml1-5">Temperature</h4>
        <bh-legends
          :primaryModel="primaryModel"
          :legends="tabOptions['temperature'].legends"
          v-if="!minimize"
        />
      </div>
      <bh-chart
        :options="optionsData['temperature']"
        @onBrushEnd="onBrushEnd"
        ref="temperature"
        enableSyncZoom
        @chartClicked="chartClicked"
      />
    </div>
    <div class="h30 mt--2" v-if="optionsData['butane'] && vitalsLength">
      <div class="mr2 display-flex justify-between">
        <h4 class="ml1-5">Butane</h4>
        <bh-legends
          :primaryModel="primaryModel"
          :legends="tabOptions['butane'].legends"
          v-if="!minimize"
        />
      </div>
      <bh-chart
        :options="optionsData['butane']"
        @onBrushEnd="onBrushEnd"
        ref="butane"
        enableSyncZoom
        @chartClicked="chartClicked"
      />
    </div>
    <div class="h30 mt--2" v-if="optionsData['carbonDioxide'] && vitalsLength">
      <div class="mr2 display-flex justify-between">
        <h4 class="ml1-5">CarbonDioxide</h4>
        <bh-legends
          :primaryModel="primaryModel"
          :legends="tabOptions['carbonDioxide'].legends"
          v-if="!minimize"
        />
      </div>
      <bh-chart
        :options="optionsData['carbonDioxide']"
        @onBrushEnd="onBrushEnd"
        ref="carbonDioxide"
        enableSyncZoom
        @chartClicked="chartClicked"
      />
    </div>
    <div class="h30 mt--2" v-if="optionsData['sulphurDioxide'] && vitalsLength">
      <div class="mr2 display-flex justify-between">
        <h4 class="ml1-5">SulphurDioxide</h4>
        <bh-legends
          :primaryModel="primaryModel"
          :legends="tabOptions['sulphurDioxide'].legends"
          v-if="!minimize"
        />
      </div>
      <bh-chart
        :options="optionsData['sulphurDioxide']"
        @onBrushEnd="onBrushEnd"
        ref="sulphurDioxide"
        @syncZoom="syncZoom"
        enableSyncZoom
        @chartClicked="chartClicked"
      />
    </div>
    <div
      class="display-flex align-center flex-column-mb"
      :class="{ 'justify-center': customDateSet, mt5: vitalsLength }"
    >
      <bh-horizontal-timebar
        class="ml2 ml0-5-mb mr2"
        v-if="!customDateSet || isInvalidCustomDate"
        :selectedKey="timeKey"
        :data="timesData"
        @output="onRangeClick"
      />
      <div
        :class="{
          'display-flex align-center justify-center flex-column h50': !vitalsLength
        }"
      >
        <div class="text-danger f2" v-if="!vitalsLength">No Data Available</div>
        <bh-date-time-picker
          v-if="
            (vitalsLength &&
              vitals.length &&
              (vitalsLength - 10 <= vitals.length ||
                vitals.length >= vitalsLength)) ||
              (!vitalsLength && customDateSet)
          "
          :class="{ mt5: !vitalsLength }"
          :dateRange="dateRange"
          :isInvalidCustomDate="isInvalidCustomDate"
          @pickDateAndTime="onDateAndTimePick"
          @onResetVitals="resetVitals"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Legends from './Legends'
import { tabOptions } from '@/services/device-details'
import { mapActions, mapGetters } from 'vuex'
import moment from '@/utils/moment'

export default {
  name: 'bh-vitals-chart-full-view',
  props: {
    optionsData: {
      type: Object,
      default() {
        return {}
      }
    },
    minimize: {
      type: Boolean,
      default: false
    },
    timesData: {
      type: Array | Object,
      default() {
        return {}
      }
    },
    timeKey: {
      type: String,
      default: 'All'
    },
    primaryModel: {
      type: String
    },
    isInvalidCustomDate: false,
    isCustomDate: false,
    dateRange: {}
  },
  components: {
    'bh-legends': Legends
  },
  data() {
    return {
      tabOptions,
      customDate: ''
    }
  },
  computed: {
    ...mapGetters('device', [
      'brushCoords',
      'brushCoordIndex',
      'customDateSet',
      'vitals',
      'vitalsLength'
    ])
  },
  watch: {
    brushCoordIndex(newVal, oldVal) {
      this.emitBrushEvent()
    },
    isInvalidCustomDate(newVal, oldVal) {
      if (newVal) {
        this.setToast([
          'toast',
          {
            duration: 6000,
            show: true,
            message: `No data available for ${this.customDate}`,
            color: '#F44336'
          }
        ])
        this.$emit('clearError')
      }
    },
    vitals(newValue, oldValue) {
      if (
        this.vitals &&
        this.vitalsLength &&
        this.vitals.length >= this.vitalsLength
      ) {
        setImmediate(this.emitBrushEvent)
      }
    }
  },
  methods: {
    ...mapActions({ setToast: 'setSingleProperty' }),
    ...mapActions('device', ['setSingleProperty', 'getMinMaxDate']),
    onBrushEnd(event) {
      this.$emit('onBrushEnd', { event, refs: this.$refs })
    },
    syncZoom(event) {
      this.$emit('syncZoom', { event, refs: this.$refs })
    },
    chartClicked(ev) {
      this.$emit('chartClicked', ev)
    },
    onRangeClick({ key, value }) {
      this.$emit('onTimeRange', {
        key,
        time: value,
        selectedTab: 'sulphurDioxide',
        refs: this.$refs
      })
    },
    emitBrushEvent() {
      const { startDateIndex, endDateIndex } =
        this.brushCoords[this.brushCoordIndex] || {}
      if (startDateIndex && endDateIndex) {
        this.onBrushEnd({
          areas: [{ coordRange: [startDateIndex, endDateIndex] }]
        })
      }
    },
    onDateAndTimePick(e) {
      this.setSingleProperty(['customDateSet', true])
      this.customDate = moment.formatDate(e, 'DD MMM, YYYY, HH:mm:ss')
      const selectedHour = new Date(e).getHours()
      this.$emit('onCustomDatePick', {
        startTime: new Date(new Date(e).setHours(selectedHour - 2)).getTime(),
        endTime: new Date(new Date(e).setHours(selectedHour + 2)).getTime(),
        selectedTab: 'sulphurDioxide'
      })
    },
    resetVitals() {
      this.setSingleProperty(['customDateSet', false])
      this.$emit('onResetVital', true)
    }
  }
}
</script>
